import {
  IRxTransport,
  ITxTransport,
} from "lib/deviceDrivers/sessionFramework/device/types";
import {
  DriverEventType,
  flushDriverEffects,
  type DriverEffect,
  type DriverNotifier,
} from "lib/deviceDrivers/sessionFramework/adapter/driverEffects";
import { toastNotifier } from "lib/deviceDrivers/sessionFramework/adapter/toastNotifier";

/**
 * Generic session base that bridges a device protocol handle to the Redux action pipeline.
 *
 * Two separate effect queues enforce the ordering invariant structurally:
 * domain effects (Redux state updates) are always drained before send effects
 * (outgoing protocol writes) so downstream state is consistent before the next command goes out.
 */
export class DriverSession<
  TDeviceHandle extends { destroy(): void },
  TAction,
  TEvent,
> {
  protected readonly device: TDeviceHandle;

  private readonly domainEffects: DriverEffect<TAction>[] = [];
  private readonly sendEffects: DriverEffect<TAction>[] = [];
  private receiveHandler: (payload: string) => void = () => {};

  constructor(
    protected readonly deviceId: string,
    createDevice: (
      tx: ITxTransport,
      rx: IRxTransport,
      onEvent: (event: TEvent) => void,
    ) => TDeviceHandle,
    createSendAction: (deviceId: string, payload: string) => TAction,
    mapEventToEffects: (
      deviceId: string,
      event: TEvent,
    ) => DriverEffect<TAction>[],
    protected readonly notifier: DriverNotifier = toastNotifier,
  ) {
    const sessionDeviceId = this.deviceId;

    const tx: ITxTransport = {
      send: payload => {
        this.sendEffects.push({
          type: DriverEventType.ACTION,
          action: createSendAction(sessionDeviceId, payload),
        });
        return Promise.resolve();
      },
    };

    const rx: IRxTransport = {
      onReceive: handler => {
        this.receiveHandler = handler;
        return () => {
          if (this.receiveHandler === handler) {
            this.receiveHandler = () => {};
          }
        };
      },
    };

    this.device = createDevice(tx, rx, event => {
      this.domainEffects.push(...mapEventToEffects(sessionDeviceId, event));
    });
  }

  receive(payload: string): TAction[] | undefined {
    this.receiveHandler(payload);
    return this.drainEffects();
  }

  tick(): TAction[] | undefined {
    return this.drainEffects();
  }

  destroy(): void {
    this.device.destroy();
    this.domainEffects.length = 0;
    this.sendEffects.length = 0;
  }

  protected drainEffects(): TAction[] | undefined {
    return flushDriverEffects(
      [...this.domainEffects.splice(0), ...this.sendEffects.splice(0)],
      this.notifier,
    );
  }
}

export interface DeviceLike {
  deviceId?: string;
  id?: string;
  name?: string;
}

/**
 * Extracts a device identifier from a device object, preferring `deviceId` over the fallback `id` property. The two-field check exists because the same physical scanner can arrive under either name depending on which call site sends the message.
 */
export function getDeviceId(device: DeviceLike): string | undefined {
  return device.deviceId ?? device.id;
}

export function createSessionStorage<TSession>(
  sessionFactory: new (deviceId: string) => TSession,
) {
  const sessionByDeviceId = new Map<string, TSession>();

  function getOrCreateSession(deviceId: string): TSession {
    const existing = sessionByDeviceId.get(deviceId);
    if (existing) {
      return existing;
    }
    const session = new sessionFactory(deviceId);
    sessionByDeviceId.set(deviceId, session);
    return session;
  }

  function get(deviceId: string): TSession | undefined {
    return sessionByDeviceId.get(deviceId);
  }

  /**
   * Removes the session from storage. Does not call `session.destroy()` - the caller must
   * have already destroyed the session, otherwise its subscription/resources will leak.
   */
  function dispose(deviceId: string): void {
    sessionByDeviceId.delete(deviceId);
  }
  return { getOrCreateSession, get, dispose };
}
